'use client';

import { PlanData, PlanPeriod } from 'components';
import { JSX } from 'react';

import { Text } from '@videoblocks/design-system';

interface PlanCardHeaderProps {
    plan: PlanData;
    selectedPeriod: PlanPeriod;
    currencySymbol: string;
}

const SavingsCallout = ({ savings }: { savings: PlanData['savings'] }) => {
    if (!savings) return null;
    return (
        <div className="flex items-center gap-1 px-0 py-0.5 rounded">
            <div className="w-4 h-4">
                <svg className="w-full h-full text-green-700" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" />
                </svg>
            </div>
            <Text asChild fontSize="sm" fontWeight="semibold" className="whitespace-nowrap">
                <span className="text-green-700">{savings.text}</span>
            </Text>
        </div>
    );
};

const UserCount = ({ userCount }: { userCount: number }) => {
    return (
        <div className="flex items-center gap-1 px-0 py-0.5 rounded">
            <div className="w-3.5 h-3.5 overflow-hidden">
                <svg className="w-full h-full text-gray-900" fill="currentColor" viewBox="0 0 20 20">
                    <path
                        fillRule="evenodd"
                        d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                        clipRule="evenodd"
                    />
                </svg>
            </div>
            <Text asChild fontSize="sm" fontWeight="semibold" className="whitespace-nowrap">
                <span>{userCount} user</span>
            </Text>
        </div>
    );
};

export const PlanCardHeader = ({ plan, selectedPeriod, currencySymbol }: PlanCardHeaderProps): JSX.Element => {
    const currentPrice = plan.pricing[selectedPeriod];

    return (
        <div className="flex flex-col gap-4 w-full">
            <div className="flex flex-col gap-2 w-full">
                <Text asChild fontSize="2xl" fontWeight="semibold" className="m-0">
                    <h2>{plan.name}</h2>
                </Text>
                <Text fontSize="sm" fontWeight="regular" className="m-0">
                    {plan.description}
                </Text>

                <div className="flex items-center gap-2">
                    <UserCount userCount={plan.userCount} />
                    <SavingsCallout savings={plan.savings} />
                </div>
            </div>

            <div className="flex items-center gap-1">
                {selectedPeriod === 'annual' && plan.originalPricing?.annual && (
                    <div className="flex items-start relative">
                        <Text asChild fontSize="2xl" fontWeight="semibold" className="m-0">
                            <span className="text-gray-600 whitespace-nowrap">
                                <Text asChild fontSize="sm" fontWeight="regular" className="m-0">
                                    <span>{currencySymbol}</span>
                                </Text>
                                {plan.originalPricing.annual}
                            </span>
                        </Text>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-8 h-0 border-t border-gray-600 rotate-[53.8deg] transform-gpu" />
                        </div>
                    </div>
                )}
                <div className="flex items-start">
                    <Text asChild fontSize="4xl" fontWeight="semibold" className="m-0">
                        <span className="text-gray-900 whitespace-nowrap">
                            {currencySymbol}
                            {currentPrice}
                        </span>
                    </Text>
                </div>
                <div className="basis-0 grow min-w-0 min-h-px">
                    <Text fontSize="sm" fontWeight="regular" className="m-0">
                        per month
                    </Text>
                    <Text fontSize="sm" fontWeight="regular" className="m-0">
                        {selectedPeriod === 'annual' ? 'billed annually' : ''}
                    </Text>
                </div>
            </div>
        </div>
    );
};
